import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SecurityTab = ({ getAuthHeaders }) => {
  const [activeSecurityTab, setActiveSecurityTab] = useState('violations');
  const [violations, setViolations] = useState([]);
  const [blockedIPs, setBlockedIPs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [blockIPForm, setBlockIPForm] = useState({
    ip_address: '',
    reason: '',
    duration_hours: ''
  });

  const fetchViolations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/rate-limit-violations`, {
        headers: getAuthHeaders(),
        params: { limit: 100 }
      });
      setViolations(response.data.violations || []);
    } catch (err) {
      console.error('Failed to fetch violations:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockedIPs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/blocked-ips`, {
        headers: getAuthHeaders(),
        params: { limit: 100 }
      });
      setBlockedIPs(response.data.blocked_ips || []);
    } catch (err) {
      console.error('Failed to fetch blocked IPs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeSecurityTab === 'violations') {
      fetchViolations();
    } else if (activeSecurityTab === 'blocked') {
      fetchBlockedIPs();
    }
  }, [activeSecurityTab]);

  const handleBlockIP = async (e) => {
    e.preventDefault();
    
    if (!blockIPForm.ip_address || !blockIPForm.reason) {
      alert('IP address and reason are required');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/block-ip`,
        {
          ip_address: blockIPForm.ip_address,
          reason: blockIPForm.reason,
          duration_hours: blockIPForm.duration_hours ? parseInt(blockIPForm.duration_hours) : null
        },
        { headers: getAuthHeaders() }
      );
      
      alert('IP blocked successfully');
      setBlockIPForm({ ip_address: '', reason: '', duration_hours: '' });
      fetchBlockedIPs();
      setActiveSecurityTab('blocked');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to block IP');
    }
  };

  const handleUnblockIP = async (ipAddress) => {
    if (!window.confirm(`Are you sure you want to unblock ${ipAddress}?`)) {
      return;
    }

    try {
      await axios.post(
        `${API}/admin/unblock-ip/${ipAddress}`,
        {},
        { headers: getAuthHeaders() }
      );
      alert('IP unblocked successfully');
      fetchBlockedIPs();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to unblock IP');
    }
  };

  return (
    <div>
      {/* Security Sub-tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex space-x-4">
          <button
            onClick={() => setActiveSecurityTab('violations')}
            className={`pb-2 px-4 font-medium ${
              activeSecurityTab === 'violations'
                ? 'border-b-2 border-red-600 text-red-600'
                : 'text-gray-600'
            }`}
          >
            Rate Limit Violations ({violations.length})
          </button>
          <button
            onClick={() => setActiveSecurityTab('blocked')}
            className={`pb-2 px-4 font-medium ${
              activeSecurityTab === 'blocked'
                ? 'border-b-2 border-red-600 text-red-600'
                : 'text-gray-600'
            }`}
          >
            Blocked IPs ({blockedIPs.length})
          </button>
          <button
            onClick={() => setActiveSecurityTab('block')}
            className={`pb-2 px-4 font-medium ${
              activeSecurityTab === 'block'
                ? 'border-b-2 border-red-600 text-red-600'
                : 'text-gray-600'
            }`}
          >
            Block New IP
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      )}

      {/* Rate Limit Violations */}
      {activeSecurityTab === 'violations' && !loading && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900">Recent Rate Limit Violations</h3>
            <p className="text-sm text-gray-600 mt-1">
              IPs that have triggered rate limits (7 quote submissions/hour or 5 login attempts/15 mins)
            </p>
          </div>

          {violations.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              No rate limit violations recorded yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Endpoint</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Violations</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">First Violation</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Violation</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User Agent</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {violations.map((violation, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                        {violation.ip_address}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {violation.endpoint}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full font-semibold">
                          {violation.violation_count}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {new Date(violation.first_violation).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {new Date(violation.last_violation).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 truncate max-w-xs">
                        {violation.user_agent || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Blocked IPs */}
      {activeSecurityTab === 'blocked' && !loading && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900">Manually Blocked IP Addresses</h3>
            <p className="text-sm text-gray-600 mt-1">
              IPs that have been manually blocked by administrators
            </p>
          </div>

          {blockedIPs.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              No IPs are currently blocked.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Blocked By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Blocked At</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires At</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {blockedIPs.map((blocked, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-semibold text-red-600">
                        {blocked.ip_address}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {blocked.reason}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {blocked.blocked_by}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {new Date(blocked.blocked_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {blocked.expires_at ? new Date(blocked.expires_at).toLocaleString() : 'Permanent'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleUnblockIP(blocked.ip_address)}
                          className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-xs font-medium"
                        >
                          Unblock
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Block New IP Form */}
      {activeSecurityTab === 'block' && (
        <div className="bg-white rounded-lg shadow p-6 max-w-2xl">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Block IP Address</h3>
          <form onSubmit={handleBlockIP} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                IP Address *
              </label>
              <input
                type="text"
                value={blockIPForm.ip_address}
                onChange={(e) => setBlockIPForm({ ...blockIPForm, ip_address: e.target.value })}
                placeholder="192.168.1.1"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason *
              </label>
              <textarea
                value={blockIPForm.reason}
                onChange={(e) => setBlockIPForm({ ...blockIPForm, reason: e.target.value })}
                placeholder="E.g., Repeated rate limit violations, suspicious activity..."
                required
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Block Duration (hours)
              </label>
              <input
                type="number"
                value={blockIPForm.duration_hours}
                onChange={(e) => setBlockIPForm({ ...blockIPForm, duration_hours: e.target.value })}
                placeholder="Leave empty for permanent block"
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
              <p className="mt-1 text-sm text-gray-500">
                Leave empty for a permanent block. Otherwise, specify hours (e.g., 24 for 1 day, 168 for 1 week).
              </p>
            </div>

            <div className="flex space-x-3 pt-2">
              <button
                type="submit"
                className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 font-medium"
              >
                Block IP Address
              </button>
              <button
                type="button"
                onClick={() => setBlockIPForm({ ip_address: '', reason: '', duration_hours: '' })}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400 font-medium"
              >
                Clear Form
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default SecurityTab;
